import { createAsyncThunk } from '@reduxjs/toolkit';

import { AdminApiPaths } from '../../../api/APIPaths';
import APIDataProvider, { TApiResponse } from '../../../api/ApiDataProvider';
import {
  IMeetingRequest,
  TMeetingRequestTutorAmountData,
  TMeetingRequestStatus,
  TMeetingRequestTutorPaymentStatus,
  TMeetingRequestType,
} from '.';
import { TLessonTypes } from '../SectionSlice';

export type TAdminRespondMeetingRequestResponse =
  | 'APPROVE'
  | 'REJECT'
  | 'UPDATE';

export type TAdminGetMeetingRequests = {
  limit: number;
  offset: number;
  statuses?: TMeetingRequestStatus[];
  sortKey?: string;
  sortValue?: number;
  studentId?: string;
  tutorId?: string;
  requestedByRole?: string;
  startDate?: string;
  endDate?: string;
  type?: TMeetingRequestType;
  lessonType?: TLessonTypes;
  includeTutorAmountData?: boolean;
};

export type TAdminRespondMeetingRequest = {
  requestId: string;
  response: TAdminRespondMeetingRequestResponse;
  penalty?: number;
  penaltyReason?: string;
  adminNote?: string;
};

export type TAdminUpdateMeetingRequestTutorPaymentStatus = {
  meetingRequestIds: string[];
  tutorPaymentStatus: TMeetingRequestTutorPaymentStatus;
};

const apiDataProvider = new APIDataProvider();

export const ADMIN_RESPOND_MEETING_REQUEST_RESPONSE_TYPES = {
  APPROVE: 'APPROVE' as TAdminRespondMeetingRequestResponse,
  REJECT: 'REJECT' as TAdminRespondMeetingRequestResponse,
  UPDATE: 'UPDATE' as TAdminRespondMeetingRequestResponse,
};

const adminRespondMeetingRequestModule = async (
  data: TAdminRespondMeetingRequest
) => {
  const path = `${AdminApiPaths.meetingRequests}/respond/${data.requestId}`;

  const response = await apiDataProvider.put<TApiResponse<IMeetingRequest>>(
    path,
    data
  );

  return response.data;
};

export const adminGetMeetingRequests = createAsyncThunk(
  '/admin/meeting-requests/list',
  async ({
    limit,
    offset,
    sortKey,
    sortValue,
    statuses,
    studentId,
    tutorId,
    requestedByRole,
    startDate,
    endDate,
    type,
    lessonType,
    includeTutorAmountData,
  }: TAdminGetMeetingRequests) => {
    let path = `${AdminApiPaths.meetingRequests}?limit=${limit}&offset=${offset}`;

    if (statuses) {
      path += `&statuses=${statuses}`;
    }

    if (sortKey) {
      path += `&sortKey=${sortKey}`;
    }

    if (sortValue) {
      path += `&sortValue=${sortValue}`;
    }

    if (studentId) {
      path += `&studentId=${studentId}`;
    }

    if (tutorId) {
      path += `&tutorId=${tutorId}`;
    }

    if (requestedByRole) {
      path += `&requestedByRole=${requestedByRole}`;
    }

    if (startDate) {
      path += `&startDate=${startDate}`;
    }

    if (endDate) {
      path += `&endDate=${endDate}`;
    }

    if (type) {
      path += `&type=${type}`;
    }

    if (lessonType) {
      path += `&lessonType=${lessonType}`;
    }

    if (includeTutorAmountData) {
      path += `&includeTutorAmountData=1`;
    }

    const response = await apiDataProvider.get<
      TApiResponse<{
        offset: number;
        limit: number;
        totalDocs: number;
        docs: Array<IMeetingRequest>;
        tutorAmountData?: TMeetingRequestTutorAmountData;
      }>
    >(path);

    return response.data;
  }
);

export const adminGetMeetingRequestById = createAsyncThunk(
  '/admin/meeting-requests/getOne',
  async ({ id }: { id: string }) => {
    const path = `${AdminApiPaths.meetingRequests}/${id}`;

    const response = await apiDataProvider.get<TApiResponse<IMeetingRequest>>(
      path
    );

    return response.data;
  }
);

export const adminUpdateMeetingRequestTutorPaymentStatus = createAsyncThunk(
  '/admin/meeting-requests/update-tutor-payment-status',
  async (data: TAdminUpdateMeetingRequestTutorPaymentStatus) => {
    const response = await apiDataProvider.put<
      TApiResponse<TAdminUpdateMeetingRequestTutorPaymentStatus>
    >(`${AdminApiPaths.meetingRequests}/update-tutor-payment-status`, data);

    return response.data;
  }
);

export const adminRespondMeetingRequest = createAsyncThunk(
  '/admin/meeting-requests/respond/id',
  adminRespondMeetingRequestModule
);

export const adminUpdateMeetingRequest = createAsyncThunk(
  '/admin/meeting-requests/update-respond/id',
  adminRespondMeetingRequestModule
);
